import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./ResourceDetail.css";
import { Spinner } from "../../../components/Filtering/Reusable/Spinner";
import { useTranslation } from "react-i18next";
import DropDownOfferStatus from "../../../components/buttons/DropDownOfferStatus";
import DropDownFrecuency from "../../../components/buttons/DropDownFrecuency";
import DropDownLanguage from "../../../components/buttons/DropDownLanguage";
import DropDownCountry from "../../../components/buttons/DropDownCountry";
import DropDownTheme from "../../../components/buttons/DropDownTheme";
import DropDownType from "../../../components/buttons/DropDownType";
import DropDownMediaType from "../../../components/buttons/DropDownMediaType";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import BreadCrumb from "../../../components/HeaderComponents/BreadCrum";
import DeleteButton from "../../../components/buttons/DeleteButton";
import {
  toggleSection,
  validateURL,
  validateVersion,
  getControlledList,
  getBackendLangCode,
} from "../../../utils/constants";
import {
  setTextForField,
  getTextForField,
  removeLanguageTab,
  addLanguageTab,
} from "../../../utils/formUtils";
import ArrowIcon from "../../../utils/ArrowIcon";
import { useAuthRoles } from "../../../hooks/useAuthRoles";
import { getUniqueSortedCountries } from "../../../utils/countriesOrder";
import { useWorkerApiHelper } from "../../../hooks/useWorkerApiHelper";
import MultiLang from "../../../components/MultiLang/MultiLang";
import KeywordsInput from "../../../components/Keywords/KeywordsInput";
import { availableLanguages } from "../../../i18n";
import { showErrorAlert } from "../../../utils/showErrorAlert";
import { showSuccessToast } from "../../../utils/showToastAlert";
import { getParticipantIdFromToken } from "../../DataOfferings/Crud/offeringFormUtils";
import { useGetParticipantByParticipantId } from "../../../hooks/participants/useGetParticipantByParticipantId";

function ResourceDetail() {
  const { callApiWithWorker } = useWorkerApiHelper();
  const [isGeneralOpen, setIsGeneralOpen] = useState(true);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [, setUrlValue] = useState("");
  const [urlError, setUrlError] = useState("");
  const [urlLicenseError, setUrlLicenseError] = useState("");
  const [urlLandingError, setUrlLandingError] = useState("");
  const [versionFormat, setVersionFormat] = useState("");
  const [fileSelected, setFileSelected] = useState(false);
  const fileInputRef = useRef(null);
  const [fileName, setFileName] = useState("");
  const { t, i18n } = useTranslation();
  const currentLang = getBackendLangCode(i18n.language);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isUrlValid, setIsUrlValid] = useState(false);
  const [baseLang] = useState(() => i18n.language);
  const [openModal, setOpenModal] = useState(false);
  const [modalMessage] = useState("");
  const [resourceMethod, setResourceMethod] = useState("url");
  const [languageTabs, setLanguageTabs] = useState([]);
  const [activeLang, setActiveLang] = useState("");
  const { decoded } = useAuthRoles();
  const participantName = decoded.organization;

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const [controlledVocabularies, setControlledVocabularies] = useState({
    frequencies: [],
    mediaTypes: [],
    countries: [],
    languages: [],
    themes: [],
    types: [],
    status: [],
  });

  const participantIdFromToken = getParticipantIdFromToken(decoded);

  const { data: assignerParticipant } =
    useGetParticipantByParticipantId({
      callApiWithWorker,
      participantId: participantIdFromToken,
    });

  const [formData, setFormData] = useState({
    type: "",
    resource_url: "",
    resource_file: "",
    title: [{ language: baseLang, value: "" }],
    version_info: "",
    resource_extension: "",
    metadata: {
      description: [{ language: baseLang, value: "" }],
      version_notes: [{ language: baseLang, value: "" }],
      publisher: assignerParticipant?.id || "",
      author: [],
      license: "",
      status: "",
      spatial: [],
      theme: [],
      frequency: "",
      language: [],
      documentation: [],
    },
  });

  const [isFormValid, setIsFormValid] = useState(false);

  useEffect(() => {
    const publisherId = String(assignerParticipant?.id || "").trim();
    if (!publisherId) return;

    setFormData((prev) => {
      if (String(prev.metadata?.publisher || "").trim()) return prev;

      return {
        ...prev,
        metadata: {
          ...prev.metadata,
          publisher: publisherId,
        },
      };
    });
  }, [assignerParticipant]);

  useEffect(() => {
    const requiredFieldsBase = [
      "type",
      "title",
      "version_info",
      "resource_extension",
      "metadata.description",
      "metadata.version_notes",
      "metadata.publisher",
    ];

    const requiredFields =
      resourceMethod === "url"
        ? [...requiredFieldsBase, "resource_url"]
        : [...requiredFieldsBase, "resource_file"];

    const isMandatoryFieldsFilled = requiredFields.every((field) => {
      const [mainField, subField] = field.split(".");
      let value;

      if (subField) {
        value = formData[mainField]?.[subField];
      } else {
        value = formData[mainField];
      }

      if (Array.isArray(value)) {
        return value.some((v) => {
          if (typeof v === "string") return v.trim() !== "";
          if (typeof v === "object" && v) {
            const txt = v.value ?? v.text ?? "";
            return String(txt).trim() !== "";
          }
          return false;
        });
      }

      return typeof value === "string" && value.trim() !== "";
    });

    const hasErrors =
      (urlError && formData.resource_url.trim() !== "") ||
      (urlLicenseError && formData.metadata.license?.trim() !== "") ||
      (urlLandingError &&
        (Array.isArray(formData.metadata.documentation)
          ? formData.metadata.documentation.some(
              (v) =>
                (typeof v === "string" && v.trim() !== "") ||
                (typeof v === "object" &&
                  ((v.value && v.value.trim() !== "") ||
                    (v.text && v.text.trim() !== ""))),
            )
          : typeof formData.metadata.documentation === "string" &&
            formData.metadata.documentation.trim() !== "")) ||
      (versionFormat && formData.version_info.trim() !== "");

    const urlRequirementOk = resourceMethod === "file" ? true : isUrlValid;

    setIsFormValid(isMandatoryFieldsFilled && urlRequirementOk && !hasErrors);
  }, [
    formData,
    isUrlValid,
    urlError,
    urlLicenseError,
    urlLandingError,
    versionFormat,
    resourceMethod,
  ]);

  const translatableFields = [
    "title",
    "metadata.description",
    "metadata.version_notes",
  ];

  const handleUnifiedChange = (namePathOrEvent, valueOrEvent) => {
    let namePath, value;

    if (valueOrEvent === undefined) {
      const e = namePathOrEvent;
      if (e && e.target) {
        const { name, type, value: targetValue, files } = e.target;
        namePath = name;
        value = type === "file" ? files[0] || "" : targetValue;
      }
    } else {
      namePath = namePathOrEvent;
      if (valueOrEvent && valueOrEvent.target) {
        const { type, value: targetValue, files } = valueOrEvent.target;
        value = type === "file" ? files[0] || "" : targetValue;
      } else {
        value = valueOrEvent;
      }
    }

    if (translatableFields.includes(namePath)) {
      let normalized = value;
      if (
        ["metadata.description", "metadata.version_notes"].includes(namePath) &&
        typeof normalized === "string"
      ) {
        normalized = normalized.replace(/\r?\n+/g, " ");
      }
      if (Array.isArray(normalized)) normalized = normalized[0] || "";
      const lang = activeLang === "" ? baseLang : activeLang;
      setFormData((prev) => setTextForField(prev, namePath, lang, normalized));
      return;
    }

    const singleElementArrayFields = [];
    const multiElementArrayFields = [
      "language",
      "spatial",
      "theme",
      "documentation",
      "author",
    ];
    const arrayFields = [
      ...singleElementArrayFields,
      ...multiElementArrayFields,
    ];

    if (typeof namePath === "string" && namePath.startsWith("metadata.")) {
      const metadataField = namePath.split(".")[1];

      setFormData((prev) => {
        let newValue = value;

        if (arrayFields.includes(metadataField)) {
          if (singleElementArrayFields.includes(metadataField)) {
            if (typeof newValue === "string") {
              newValue = [newValue];
            } else if (Array.isArray(newValue)) {
              newValue = [newValue.join(" ")];
            } else {
              newValue = [];
            }
          } else if (multiElementArrayFields.includes(metadataField)) {
            if (typeof newValue === "string") {
              newValue = newValue
                .split(/\r?\n|,/)
                .map((s) => s.trim())
                .filter(Boolean);
            } else if (Array.isArray(newValue)) {
              newValue = newValue
                .map((v) => (typeof v === "string" ? v.trim() : v))
                .filter(Boolean);
            } else {
              newValue = [];
            }
          }
        }

        return {
          ...prev,
          metadata: {
            ...prev.metadata,
            [metadataField]: newValue,
          },
        };
      });
    } else if (typeof namePath === "string") {
      setFormData((prev) => ({
        ...prev,
        [namePath]: value || "",
      }));
    } else {
      console.error("Invalid namePath:", namePath);
    }

    if (namePath === "version_info") {
      const { error } = validateVersion(value, t);
      setVersionFormat(error);
    }

    if (["metadata.license", "metadata.documentation"].includes(namePath)) {
      const origin = namePath === "metadata.license" ? "license" : "landing";

      let valueToValidate = "";
      if (Array.isArray(value)) {
        const first = value.find((v) => {
          if (typeof v === "string") return v.trim() !== "";
          if (typeof v === "object" && v !== null) {
            return (
              (v.value && v.value.trim() !== "") ||
              (v.text && v.text.trim() !== "") ||
              false
            );
          }
          return false;
        });
        if (typeof first === "string") valueToValidate = first;
        else if (typeof first === "object" && first !== null)
          valueToValidate = first.value || first.text || "";
      } else if (typeof value === "object" && value !== null) {
        valueToValidate = value.value || value.text || "";
      } else if (typeof value === "string") {
        valueToValidate = value;
      }

      const { valid, error } = validateURL(valueToValidate, t);
      setUrlErrorByOrigin(origin, error);
      setIsUrlValid(valid);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files.length > 0) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onloadend = () => {
        let base64String = reader.result;
        base64String = base64String.split(",")[1];
        setFormData((prevData) => ({
          ...prevData,
          resource_file: base64String,
          resource_url: "",
        }));
        setFileSelected(true);
        setFileName(file.name);
      };
      reader.readAsDataURL(file);
    } else {
      resetFileInput();
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      processFile(file);
      e.dataTransfer.clearData();
    }
  };

  const processFile = (file) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      let base64String = reader.result.split(",")[1];
      setFormData((prevData) => ({
        ...prevData,
        resource_file: base64String,
        resource_url: "",
      }));
      setFileSelected(true);
      setFileName(file.name);
    };
    reader.readAsDataURL(file);
  };

  const resetFileInput = () => {
    setFileSelected(false);
    setFileName("");
    setFormData((prevData) => ({
      ...prevData,
      resource_file: "",
    }));
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  const deleteFile = () => {
    resetFileInput();
  };

  const setUrlErrorByOrigin = (origin, message) => {
    switch (origin) {
      case "license":
        setUrlLicenseError(message);
        break;
      case "landing":
        setUrlLandingError(message);
        break;
      case "resource":
        setUrlError(message);
        break;
      default:
        console.warn("Unknown error origin:", origin);
    }
  };

  const handleUrlChange = (e) => {
    const value = e.target.value;
    setUrlValue(value);

    const { valid, error } = validateURL(value, t);
    setIsUrlValid(valid);
    setUrlError(error);

    setFormData((prevData) => ({
      ...prevData,
      resource_url: value,
      resource_file: "",
    }));
  };

  const handleFileClick = () => {
    fileInputRef.current.click();
  };

  const handleAuthorChange = (nextValues) => {
    const normalized = Array.isArray(nextValues) ? nextValues : [];
    setFormData((prev) => ({
      ...prev,
      metadata: { ...prev.metadata, author: normalized },
    }));
  };

  const handleDocumentationChange = (nextValues) => {
    const normalized = Array.isArray(nextValues) ? nextValues : [];

    const invalid = normalized.find((item) => {
      const value = String(item || "").trim();
      if (!value) return false;
      return !validateURL(value, t).valid;
    });

    setUrlLandingError(invalid ? validateURL(invalid, t).error : "");

    setFormData((prev) => ({
      ...prev,
      metadata: { ...prev.metadata, documentation: normalized },
    }));
  };

  useEffect(() => {
    async function fetchVocabularies() {
      const [
        mediaTypes,
        frequencies,
        status,
        countries,
        languages,
        themes,
        types,
      ] = await Promise.all([
        getControlledList(callApiWithWorker, "media-types-ext", {
          lang: i18n.language,
        }),
        getControlledList(callApiWithWorker, "frequencies", {
          lang: i18n.language,
        }),
        getControlledList(callApiWithWorker, "distribution-status", {
          lang: i18n.language,
        }),
        getControlledList(callApiWithWorker, "countries", {
          lang: i18n.language,
        }),
        getControlledList(callApiWithWorker, "languages", {
          lang: i18n.language,
        }),
        getControlledList(callApiWithWorker, "data-theme", {
          lang: i18n.language,
        }),
        getControlledList(callApiWithWorker, "resource-types", {
          lang: i18n.language,
        }),
      ]);

      setControlledVocabularies({
        frequencies,
        mediaTypes,
        countries,
        languages,
        themes,
        types,
        status,
      });
    }
    fetchVocabularies();
  }, []);

  const sortedCountries = getUniqueSortedCountries(
    controlledVocabularies.countries,
    currentLang,
  );

  const getVocabularyLabel = (item) =>
    item?.label ||
    item?.langName?.[currentLang] ||
    item?.langName?.eng ||
    item?.langName?.spa ||
    Object.values(item?.langName || {}).find(Boolean) ||
    item?.concept_iri ||
    item?.code ||
    "";

  const getVocabularyValue = (item) => item?.concept_iri || item?.code || "";

  const toggleGeneral = () =>
    toggleSection(setIsGeneralOpen, ".createdivgeneral");
  const toggleDetails = () =>
    toggleSection(setIsDetailsOpen, ".createdivdetails");

  const createResource = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await callApiWithWorker("api/vocabularyHub/create", {
        method: "POST",
        body: formData,
      });

      setLoading(false);
      showSuccessToast(t("dataModels.createdSubMessage"), {
        title: t("dataModels.createdMessage"),
      });
      navigate(-1);
    } catch (error) {
      console.error("Error en la petición:", error);
      setLoading(false);
      showErrorAlert(error);
    }
  };

  const languageOptions = availableLanguages;

  const getLangLabel = (code) =>
    languageOptions.find((o) => o.code === code)?.label || code;

  const handleAddLanguageTab = (code) => {
    addLanguageTab(
      code,
      baseLang,
      languageTabs,
      translatableFields,
      formData,
      setFormData,
      setLanguageTabs,
      setActiveLang,
    );
  };

  const handleRemoveLang = (langCode) => {
    removeLanguageTab(
      langCode,
      activeLang,
      translatableFields,
      setFormData,
      setLanguageTabs,
      setActiveLang,
    );
  };

  const renderTranslatableInput = (fieldName, placeholder, isTextarea) => {
    const value = getTextForField(formData, fieldName, activeLang, baseLang);
    const lang = activeLang === "" ? baseLang : activeLang;

    if (isTextarea) {
      return (
        <textarea
          rows="4"
          cols="20"
          className="inputoffer fontNoto"
          name={fieldName}
          placeholder={placeholder}
          value={value}
          onChange={(e) =>
            setFormData((prev) =>
              setTextForField(prev, fieldName, lang, e.target.value),
            )
          }
          required={["title", "metadata.description"].includes(fieldName)}
        />
      );
    }

    return (
      <input
        className="inputoffer"
        type="text"
        name={fieldName}
        placeholder={placeholder}
        value={value}
        onChange={(e) =>
          setFormData((prev) =>
            setTextForField(prev, fieldName, lang, e.target.value),
          )
        }
        required={["title"].includes(fieldName)}
      />
    );
  };

  const renderFieldOrDisabled = (FieldComponentOrProps) => {
    const disabled = activeLang !== "";

    if (FieldComponentOrProps && FieldComponentOrProps.custom) {
      const { component: Component, props } = FieldComponentOrProps.custom;
      return <Component {...props} disabled={disabled} />;
    }

    const {
      name,
      value,
      placeholder,
      isTextarea,
      required,
      className,
      onChange,
      ...rest
    } = FieldComponentOrProps;
    if (isTextarea) {
      return (
        <textarea
          rows="4"
          cols="20"
          className={disabled ? "inputofferdisabled" : "inputoffer fontNoto"}
          name={name}
          placeholder={placeholder}
          value={value}
          onChange={handleUnifiedChange}
          required={required}
          disabled={disabled}
        />
      );
    }
    return (
      <input
        className={
          className || (disabled ? "inputofferdisabled" : "inputoffer")
        }
        type="text"
        name={name}
        placeholder={placeholder}
        value={value}
        onChange={onChange || handleUnifiedChange}
        required={required}
        disabled={disabled}
        {...rest}
      />
    );
  };

  const renderRadioOption = (
    name,
    value,
    label,
    checked,
    onChange,
    children,
  ) => (
    <div className="radioBlock">
      <label>
        <input
          type="radio"
          name={name}
          value={value}
          checked={checked}
          onChange={onChange}
        />
        {label}
      </label>
      <div className="resourceInput">{children}</div>
    </div>
  );

  const renderUrlInput = () => {
    return renderFieldOrDisabled({
      name: "resource_url",
      value: formData.resource_url,
      placeholder: t("createDataModelDetail.detail.resource_url_placeholder"),
      isTextarea: false,
      required: false,
      onChange: handleUrlChange,
    });
  };

  return (
    <section className="grisback pb4">
      <div className="whiteback W100">
        <div>
          <BreadCrumb />
        </div>
      </div>
      <div className="containerflex">
        <div className="whiteback W100 padding2rem">
          <div className="w90">
            <h2 className="tittlecatalog">
              {t("createDataModelDetail.title")}
            </h2>
            <MultiLang
              t={t}
              baseLang={baseLang}
              languageTabs={languageTabs}
              languageOptions={languageOptions}
              activeLang={activeLang}
              addLanguageTab={handleAddLanguageTab}
              removeLanguageTab={handleRemoveLang}
              setActiveLang={setActiveLang}
              getLangLabel={getLangLabel}
            />
          </div>
        </div>
        <div className="W100 containerflex ptb2">
          <div className="w95 bgwhite">
            <div className="creatediv">
              <div
                className="createdivgeneral w100"
                onClick={toggleGeneral}
                style={{ cursor: "pointer" }}
              >
                <h4 className="ptb3lr4">{t("createDataModelDetail.fill")}</h4>
                <ArrowIcon open={isGeneralOpen} />
              </div>
              {isGeneralOpen && (
                <div
                  className="input-fields"
                  style={{ transition: "all 0.3s ease" }}
                >
                  <div className="resource-info">
                    <div className="form-rows">
                      <div className="form-row">
                        <div className="form-group">
                          <p className="ptitleinput">
                            {t("createDataModelDetail.detail.title")}
                            <span className="required">*</span>
                          </p>
                          {renderTranslatableInput(
                            "title",
                            t("createDataModelDetail.detail.title_placeholder"),
                            false,
                          )}
                        </div>
                        <div className="form-group">
                          <KeywordsInput
                            label={t("createDataModelDetail.metadata.author")}
                            value={formData.metadata.author}
                            onChange={handleAuthorChange}
                            placeholder={t(
                              "editResource.general.author_placeholder",
                            )}
                            disabled={activeLang !== ""}
                            variant="resourceForm"
                          />
                        </div>
                        <div className="form-group">
                          <p className="ptitleinput">
                            {t("createDataModelDetail.detail.resource_version")}
                            <span className="required">*</span>
                          </p>
                          {renderFieldOrDisabled({
                            name: "version_info",
                            value: formData.version_info,
                            placeholder: t(
                              "editResource.general.version_placeholder",
                            ),
                            isTextarea: false,
                            required: false,
                          })}
                          {versionFormat && (
                            <p className="error_text">{versionFormat}</p>
                          )}
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <p className="ptitleinput">
                            {t("createDataModelDetail.detail.format")}
                            <span className="required">*</span>
                          </p>

                          <DropDownMediaType
                            value={formData.resource_extension}
                            onChange={
                              activeLang === ""
                                ? (value) =>
                                    handleUnifiedChange(
                                      "resource_extension",
                                      value,
                                    )
                                : () => {}
                            }
                            options={controlledVocabularies.mediaTypes.map(
                              (item) => getVocabularyLabel(item),
                            )}
                            optionsValue={controlledVocabularies.mediaTypes.map(
                              (item) => item?.code || item?.concept_iri || "",
                            )}
                            disabled={activeLang !== ""}
                          />
                        </div>
                        <div className="form-group">
                          <p className="ptitleinput">
                            {t("createDataModelDetail.metadata.license")}
                          </p>
                          {renderFieldOrDisabled({
                            name: "metadata.license",
                            value: formData.metadata.license,
                            placeholder: t(
                              "createDataModelDetail.detail.resource_url_placeholder",
                            ),
                            isTextarea: false,
                            required: false,
                          })}
                          {urlLicenseError && (
                            <p className="error_text">{urlLicenseError}</p>
                          )}
                        </div>
                        <div className="form-group">
                          <p className="ptitleinput">
                            {t("editResource.general.status")}
                          </p>
                          <DropDownOfferStatus
                            value={formData.metadata.status}
                            onChange={
                              activeLang === ""
                                ? (e) =>
                                    handleUnifiedChange(
                                      "metadata.status",
                                      e.target.value,
                                    )
                                : () => {}
                            }
                            name="metadata.status"
                            options={controlledVocabularies.status.map((item) =>
                              getVocabularyLabel(item),
                            )}
                            translationKey="dropdown.offer_status"
                            optionsValue={controlledVocabularies.status.map(
                              (item) => getVocabularyValue(item),
                            )}
                            disabled={activeLang !== ""}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="resourceUrl">
                    <p className="ptitleinput">
                      {t("createDataModelDetail.detail.select_method")}
                      <span className="required">*</span>
                    </p>
                    <div className="radioOptions">
                      {renderRadioOption(
                        "resourceMethod",
                        "url",
                        t("createDataModelDetail.detail.resource_url"),
                        resourceMethod === "url",
                        () => {
                          setResourceMethod("url");
                          deleteFile();
                          setFormData((prev) => ({
                            ...prev,
                            resource_file: null,
                          }));
                        },
                        <>
                          {renderUrlInput()}
                          {urlError && <p className="error_text">{urlError}</p>}
                        </>,
                      )}

                      {renderRadioOption(
                        "resourceMethod",
                        "file",
                        t("createDataModelDetail.detail.resource_file"),
                        resourceMethod === "file",
                        () => {
                          setResourceMethod("file");
                          setFormData((prev) => ({
                            ...prev,
                            resource_url: "",
                          }));
                          setUrlError("");
                        },
                        <div
                          className={`dropZone ${
                            resourceMethod !== "file" ? "disabled" : ""
                          }`}
                          onDragOver={(e) => {
                            if (resourceMethod === "file") e.preventDefault();
                          }}
                          onDrop={(e) => {
                            if (resourceMethod === "file") handleDrop(e);
                          }}
                        >
                          <input
                            type="file"
                            ref={fileInputRef}
                            style={{ display: "none" }}
                            onChange={handleFileChange}
                            disabled={resourceMethod !== "file"}
                          />

                          {fileSelected ? (
                            <>
                              <DeleteButton onClick={deleteFile} />
                              <p className="fileName">{fileName}</p>
                            </>
                          ) : (
                            <button
                              className="customFileButton"
                              onClick={handleFileClick}
                              disabled={resourceMethod !== "file"}
                            >
                              {t("createDataModelDetail.detail.upload")}
                            </button>
                          )}
                        </div>,
                      )}
                    </div>
                  </div>
                </div>
              )}
              <div
                className="createdivdetails w100"
                onClick={toggleDetails}
                style={{ cursor: "pointer" }}
              >
                <h4 className="ptb3lr4">
                  {t("editResource.sections.details")}
                </h4>
                <ArrowIcon open={isDetailsOpen} />
              </div>
              {isDetailsOpen && (
                <div
                  className="input-fields"
                  style={{ transition: "all 0.3s ease" }}
                >
                  <div className="details">
                    <div className="form-columns">
                      <div className="form-column">
                        <div className="form-group-column">
                          <p className="ptitleinput">
                            {t("createDataModelDetail.metadata.description")}
                            <span className="required">*</span>
                          </p>
                          {renderTranslatableInput(
                            "metadata.description",
                            t("editResource.details.description_placeholder"),
                            true,
                          )}
                        </div>
                        <div className="form-group-column">
                          <p className="ptitleinput">
                            {t("dataOfferingsDetail.detail.publisher")}
                            <span className="required">*</span>
                          </p>
                          <input
                            className="inputofferdisabled"
                            type="text"
                            name="publisher"
                            disabled
                            value={participantName}
                          />
                        </div>
                        <div className="form-group-column">
                          <KeywordsInput
                            label={t(
                              "createDataModelDetail.detail.landing_page_url",
                            )}
                            value={formData.metadata.documentation}
                            onChange={handleDocumentationChange}
                            placeholder={t(
                              "createDataModelDetail.detail.landing_page_url_placeholder",
                            )}
                            disabled={activeLang !== ""}
                            variant="resourceForm"
                          />
                        </div>
                        {urlLandingError && (
                          <p className="error_text">{urlLandingError}</p>
                        )}
                      </div>
                      <div className="form-column">
                        <div className="form-group-column">
                          <p className="ptitleinput">
                            {t("editResource.details.version_notes")}
                            <span className="required">*</span>
                          </p>
                          {renderTranslatableInput(
                            "metadata.version_notes",
                            t("editResource.details.version_notes_placeholder"),
                            true,
                          )}
                        </div>
                        <div className="form-group-column">
                          <p className="ptitleinput">
                            {t("editResource.details.update_frequency")}
                          </p>
                          <DropDownFrecuency
                            value={formData.metadata.frequency}
                            onChange={
                              activeLang === ""
                                ? (nextValue) =>
                                    handleUnifiedChange(
                                      "metadata.frequency",
                                      nextValue,
                                    )
                                : () => {}
                            }
                            options={controlledVocabularies.frequencies.map(
                              (item) => getVocabularyLabel(item),
                            )}
                            optionsValue={controlledVocabularies.frequencies.map(
                              (item) => getVocabularyValue(item),
                            )}
                            disabled={activeLang !== ""}
                          />
                        </div>
                        <div className="form-group-column">
                          <p className="ptitleinput">
                            {t("createDataModelDetail.metadata.theme")}
                          </p>
                          <DropDownTheme
                            value={formData.metadata.theme}
                            onChange={
                              activeLang === ""
                                ? (themeArray) =>
                                    handleUnifiedChange(
                                      "metadata.theme",
                                      themeArray,
                                    )
                                : () => {}
                            }
                            options={controlledVocabularies.themes.map((item) =>
                              getVocabularyLabel(item),
                            )}
                            optionsValue={controlledVocabularies.themes.map(
                              (item) => getVocabularyValue(item),
                            )}
                            disabled={activeLang !== ""}
                          />
                        </div>
                      </div>
                      <div className="form-column column-tight">
                        <div className="form-group-column">
                          <p className="ptitleinput">
                            {t("createDataModelDetail.detail.type")}
                            <span className="required">*</span>
                          </p>
                          <DropDownType
                            value={formData.type}
                            onChange={
                              activeLang === ""
                                ? (nextValue) =>
                                    handleUnifiedChange("type", nextValue)
                                : () => {}
                            }
                            options={controlledVocabularies.types.map((item) =>
                              getVocabularyLabel(item),
                            )}
                            optionsValue={controlledVocabularies.types.map(
                              (item) => item?.code || item?.concept_iri || "",
                            )}
                            disabled={activeLang !== ""}
                          />
                        </div>
                        <div className="form-group-column">
                          <p className="ptitleinput">
                            {t("createDataModelDetail.metadata.language")}
                          </p>
                          <DropDownLanguage
                            value={formData.metadata.language}
                            onChange={
                              activeLang === ""
                                ? (languageArray) =>
                                    handleUnifiedChange(
                                      "metadata.language",
                                      languageArray,
                                    )
                                : () => {}
                            }
                            options={controlledVocabularies.languages.map(
                              (item) => getVocabularyLabel(item),
                            )}
                            optionsValue={controlledVocabularies.languages.map(
                              (item) => getVocabularyValue(item),
                            )}
                            disabled={activeLang !== ""}
                          />
                        </div>
                        <div className="form-group-column">
                          <p className="ptitleinput">
                            {t("editResource.details.geographical_coverage")}
                          </p>
                          <DropDownCountry
                            value={formData.metadata.spatial}
                            onChange={
                              activeLang === ""
                                ? (geographicalCoverageArray) =>
                                    handleUnifiedChange(
                                      "metadata.spatial",
                                      geographicalCoverageArray,
                                    )
                                : () => {}
                            }
                            placeholderText={t("dropdown.coverage")}
                            options={sortedCountries.map((item) => item.label)}
                            optionsValue={sortedCountries.map(
                              (item) => item.value,
                            )}
                            disabled={activeLang !== ""}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="whiteback W100 p2">
        <div className="w90 flexdivbotones">
          <button
            className="boton botoncancel"
            onClick={() => {
              navigate(-1);
            }}
          >
            {t("createDataOfferingsDetail.cancel")}
          </button>
          <button
            type="submit"
            className={`boton botoncapply ${
              !isFormValid ? "disabled-button" : ""
            }`}
            disabled={!isFormValid}
            onClick={createResource}
          >
            {t("createDataModelDetail.create")}
          </button>
        </div>
      </div>
      <div>
        {loading && (
          <div className="loading">
            <Spinner size="large" color="var(--main-color)" />
          </div>
        )}
      </div>
      <Dialog open={openModal} onClose={handleCloseModal}>
        <DialogTitle>
          {t("createDataModelDetail.messages.information")}
        </DialogTitle>
        <DialogContent>{modalMessage}</DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>OK</Button>
        </DialogActions>
      </Dialog>
    </section>
  );
}

export default ResourceDetail;
